import { computed, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import * as NavigationConstants from '@mwe/constants';
import {
  Address,
  AddressProducts,
  ClearingAccount,
  createAddress,
  ILSystem,
  NavigatingBackTo,
  Product,
  Sparte,
  UnpaidInvoices,
} from '@mwe/models';
import { NavigationStateService } from '../../cache/navigation-state.service';
import { CustomerDataChangeLogic } from '../../customer/change/customer-data-change.logic';
import { FindPaymentSolutionLogic } from '../../invoices/find-payment-solution/find-payment-solution.logic';
import { InteractiveInvoicesLogic } from '../../invoices/interactive-invoices/interactive-invoices.logic';
import { InvoiceDataChangeLogic } from '../../invoices/invoice-data/change/invoice-data-change.logic';
import { MoveOutLogic } from '../../move-out/move-out.logic';
import { AbstractTariffChangeLogic } from '../../tariff/abstract-tariff-change.logic';
import { VoucherRedeemLogic } from '../../voucher/redeem/voucher-redeem.logic';
import { ConsumptionStateService } from '../consumption/consumption-state.service';
import { ProductDetailsStateService } from './product-details-state.service';
import { ClearingAccountLogic } from '../../invoices/clearing-account/clearing-account.logic';
import { RelocationStore } from '../../relocation/relocation.store';
import { productStore } from '../product.store';
import { EmobCancellationStore } from '../../emob-cancellation/emob-cancellation.store';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailsLogic {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly navigationStateService = inject(NavigationStateService);
  private readonly productDetailsStateService = inject(ProductDetailsStateService);
  private readonly relocationStore = inject(RelocationStore);
  private readonly invoiceDataChangeLogic = inject(InvoiceDataChangeLogic);
  private readonly customerDataChangeLogic = inject(CustomerDataChangeLogic);
  private readonly consumptionStateService = inject(ConsumptionStateService);
  private readonly moveOutLogic = inject(MoveOutLogic);
  private readonly voucherRedeemLogic = inject(VoucherRedeemLogic);
  private readonly interactiveInvoicesLogic = inject(InteractiveInvoicesLogic);
  private readonly findPaymentSolutionLogic = inject(FindPaymentSolutionLogic);
  private readonly clearingAccountLogic = inject(ClearingAccountLogic);
  private readonly productStore = inject(productStore);
  private readonly emobCancellationStore = inject(EmobCancellationStore);
  private readonly activeProducts = computed(() => this.productStore.activeProducts());

  get product(): Product {
    return this.productDetailsStateService.product;
  }

  get address(): Address {
    return createAddress(this.productDetailsStateService.product?.address);
  }

  get products(): Product[] {
    return this.productDetailsStateService.groupProducts;
  }

  get addressProducts(): AddressProducts {
    return {
      address: this.address,
      products: this.products.filter(p => p.accountNumber === this.product?.accountNumber),
    };
  }

  get allAddressProducts(): AddressProducts {
    return {
      address: this.address,
      products: this.products,
    };
  }

  startNewClientProcess(): void {
    this.setWizardBackUrl();
    this.router.navigateByUrl(`/${NavigationConstants.NEW_CLIENT_ROUTE}`);
  }

  startRelocationProcess(): void {
    this.setWizardBackUrl();
    this.relocationStore.initRelocationProcess(this.products);
  }

  changeBankAccount(): void {
    this.setWizardBackUrl();
    this.invoiceDataChangeLogic.startBankAccountChangeWithoutAddressSelection(this.products, this.address);
  }

  changeTariffs(preselectedProduct: Product = undefined): void {
    this.setWizardBackUrl();
    const tariffChangeLogic = this.injector.get(AbstractTariffChangeLogic);
    tariffChangeLogic.initProcess(this.address, this.products, preselectedProduct);
  }

  changePdfInvoice(): void {
    this.setWizardBackUrl();
    this.invoiceDataChangeLogic.startPdfInvoiceChangeWithoutAddressSelection(this.products, this.address);
  }

  changeInvoiceAddress(): void {
    this.setWizardBackUrl();
    this.invoiceDataChangeLogic.startInvoiceAddressChangeWithoutAddressSelection(this.products, this.address);
  }

  changeCustomerData(): void {
    this.setWizardBackUrl();
    const { customerNumber } = this.product;
    const products = this.activeProducts().filter(p => p.customerNumber === customerNumber);

    this.customerDataChangeLogic.startProcessWithoutAddressSelection(products);
  }

  showConsumption(): void {
    this.setWizardBackUrl();

    this.consumptionStateService.products = [...this.products];
    this.consumptionStateService.preselectedProduct = this.product;
    this.consumptionStateService.address = this.address;
    this.consumptionStateService.directAddressSelection = true;
    this.router.navigateByUrl(`/${NavigationConstants.PRODUCTS_ROUTE}/${NavigationConstants.PRODUCTS_CONSUMPTION}`);
  }

  moveOut(isEmob = false): void {
    this.setWizardBackUrl();

    if (isEmob) {
      this.emobCancellationStore.initEmobCancellationProcess({ address: this.address, products: this.products });
      return;
    }

    const products = this.productDetailsStateService.groupProducts;
    const address = this.productDetailsStateService.address;
    this.moveOutLogic.startProcessWithoutAddressSelection(products, address);
  }

  showMeterReading(): void {
    this.setWizardBackUrl();
    this.router.navigateByUrl(`/${NavigationConstants.METER_READING_ROUTE}`);
  }

  showLastActivities(): void {
    this.addNavigatingBackTo('ProductDetails');
    this.router.navigateByUrl(`/${NavigationConstants.PROFILE_ROUTE}/${NavigationConstants.PROFILE_LAST_ACTIVITY}`);
  }

  showAccountInfoRequest(): void {
    this.setWizardBackUrl();
    this.clearingAccountLogic.startInformationRequestProcess(
      this.product.businessPartnerNumber,
      this.product.accountNumber,
      this.getAllCategoriesOfCurrentAccount(),
      this.address,
    );
  }

  redeemVoucher(): void {
    this.setWizardBackUrl();
    this.voucherRedeemLogic.redeemVoucher();
  }

  showInteractiveInvoices(): void {
    this.addNavigatingBackTo('ProductDetails');
    this.interactiveInvoicesLogic.showInteractiveInvoices(this.allAddressProducts, this.product.category);
  }

  showInvoicePartialpaymentOverview(): void {
    const addressProducts = this.addressProducts;
    const accountNumber = this.product.accountNumber;
    const systemId = this.product.systemId;

    this.addNavigatingBackTo('ProductDetails');
    this.interactiveInvoicesLogic.showInvoicePartialPaymentOverview(addressProducts, accountNumber, systemId as ILSystem);
  }

  navigateToUnpaidInvoices(unpaidInvoices: UnpaidInvoices): void {
    this.addNavigatingBackTo('ProductDetails');
    this.setWizardBackUrl();
    const categories = this.getAllCategoriesOfCurrentAccount();
    const clearingAccount: ClearingAccount = {
      accountId: this.product.accountNumber,
      systemId: this.product.systemId,
      businessPartnerId: this.product.businessPartnerNumber,
      unpaidInvoices,
    };
    this.findPaymentSolutionLogic.navigateToUnpaidInvoices(categories as Sparte[], clearingAccount, this.address);
  }

  private addNavigatingBackTo(item: NavigatingBackTo): void {
    this.navigationStateService.addNavigatingBackTo(item);
    this.productDetailsStateService.keepStorageOnce = true;
  }

  private setWizardBackUrl(): void {
    this.navigationStateService.wizardCancelUrl = `/${NavigationConstants.PRODUCTS_ROUTE}/${NavigationConstants.PRODUCTS_DETAILS_ROUTE}`;
    this.productDetailsStateService.keepStorageOnce = true;
  }

  private getAllCategoriesOfCurrentAccount(): string[] {
    return this.products
      .filter(
        gp =>
          gp?.accountNumber === this.product?.accountNumber &&
          gp?.businessPartnerNumber === this.product?.businessPartnerNumber &&
          gp?.systemId === this.product?.systemId,
      )
      .map(gp => gp.category);
  }
}
